import React from 'react'

import { ShopCountry } from '@cbgms/base/shop-id'
import { useShopCountryContext } from './shopCountryContext'

interface CommonProps {
  /** The default element to be rendered when there are no overwrites. */
  readonly default: React.ReactNode
  /** The fallback element to be rendered when there is a component that triggers `React.Suspsense`. */
  readonly fallback?: NonNullable<React.ReactNode>
}

type CountryProps = {
  readonly [index in ShopCountry]?: React.ReactNode
}

interface Props extends CommonProps, CountryProps {}

/**
 * This component renders a `React.ReactNode` for the current shop country
 * based on the shop's id.
 *
 * @example
 * ```tsx
 * <IsShopCountry
 *   default={null}
 *   fallback={<Loader />}
 *   NL={<ComponentJustForNetherlands />}
 *   GB={<ComponentJustForTheUnitedKingdom />}
 * />
 * ```
 */
export function IfShopCountry(props: Props) {
  const contextValue = useShopCountryContext()

  let element: React.ReactNode

  if (contextValue?.shopCountry) {
    element = props[contextValue?.shopCountry]
  }

  if (element === undefined) {
    element = props.default
  }

  return <React.Suspense fallback={props.fallback ?? null}>{element}</React.Suspense>
}
